// © Microsoft Corporation. All rights reserved.

import { ExperienceMilestoneEventTypes, MilestoneDataActions } from "@msnews/milestone-data";
import Foundation, { FoundationProps } from "@microsoft/fast-components-foundation-react";
import { PerfTracker, logger, markTtvr } from "@msnews/diagnostics";

import { MnsCreatorDashboardAttributes } from "./MnsCreatorDashboard.attributes";
import { appEnvironment } from "@msnews/core";
import { canUseDOM } from "@msnews/isomorphic-util";

declare global {
    interface Window {
        /**
         * Boolean flag to indicate if render cache hydration is happening
         */
        needsHydrate?: boolean;
    }
}

/** Type definition for the onExperienceMilestoneTimingUpdate callback */
export type ExperienceMilestoneTimingUpdate = (milestoneType: ExperienceMilestoneEventTypes,
    startTime?: number,
    endTime?: number) => void;

/**
 * Common props for all experiences
 */
export interface CommonExperienceProps {
    /** The experience name. */
    experienceName?: string;

    /** Custom Field per experience when sending beacon to Cosmos. */
    customField?: string;

    /**
     * Handler function for Experience Milestone timing updates
     */
    onExperienceMilestoneTimingUpdate?: ExperienceMilestoneTimingUpdate
}

/**
 * TODO: This class may need to be sampled collecting and sending Perf timings if the traffic goes beyond a certain reach.
 * Assessment will be performed later after analyzing the data.
 */
export class BaseExperience<H, U, S> extends Foundation<H, U, S> {
    /** Perf marker for tracking Visually Reading timing */
    protected visuallyReadyTracker: PerfTracker = new PerfTracker();

    /** Perf marker for tracking Initialization timing */
    protected initializedTracker: PerfTracker = new PerfTracker();

    /** Perf marker for tracking DomComplete timing */
    protected domCompleteTracker: PerfTracker = new PerfTracker();

    /**
     * The constructor.
     * @param props - The props
     * @param context - The context
     */
    constructor(props: H & U & CommonExperienceProps & FoundationProps, context?: any) {
        super(props);

        this.startInitializeMilestone();
        this.startVisuallyReadyMilestone();
        this.startDomCompleteMilestone();
    }

    /**
     * Default react hook.
     */
    public componentDidMount(): void {
        // End the domComplete milestone
        this.endDomCompleteMilestone();
    }

    /**
     * Default react hook.
     *
     * note: don't delete it, as it has different arity than the original react hook, and some components call it
     * with arity=0
     *
     * @param prevProps - previous component props
     * @param prevState - previous component state
     * @param snapshot - "snapshot" returned by getSnapshotBeforeUpdate() in React life cycle
     */
    public componentDidUpdate(prevProps: Readonly<H & U & FoundationProps> = undefined,
        prevState: Readonly<S> = undefined,
        snapshot: any = undefined): void {
        // End the domComplete milestone
        this.endDomCompleteMilestone();
    }

    /**
     * Default render function
     *
     * note: don't delete it, even if empty, as a bunch of code calls super.render()
     */
    public render(): any {
        logger.log(`Rendering ${(this.props as CommonExperienceProps).experienceName}. needsHydrate: ${canUseDOM() && window.needsHydrate}`);

        // End the initialized milestone, and start the domComplete milestone
        this.endInitializeMilestone();
        this.startDomCompleteMilestone();
    }

    /**
     * Marks the Time To Visually Ready and optionally mark perf ttvr for telemetry.
     * @param shouldMarkTelemetryTtvr - Flag to opt out from from telemetry ttvr marker
     */
    public markTimeToVisuallyReady(shouldMarkTelemetryTtvr: boolean): void {
        if (shouldMarkTelemetryTtvr) {
            markTtvr((this.props as CommonExperienceProps).experienceName);
        }

        this.endVisuallyReadyMilestone();
    }

    /**
     * Generate attributes for the mnsDashboard
     */
    protected mnsDashAttributes(): MnsCreatorDashboardAttributes {
        if (appEnvironment.ShowMnsCreatorDashboard) {
            const msnCreatorDashAttributes = {
                "data-config-href": this.props["data-config-href"],
                "data-experience-type": this.props["data-experience-type"]
            };
            return msnCreatorDashAttributes;
        } else {
            return null;
        }
    }

    /** Start the initialized perf timer, and mark the Initialized milestone start time */
    protected startInitializeMilestone(): void {
        this.initializedTracker.setStart();

        if ((this.props as CommonExperienceProps).onExperienceMilestoneTimingUpdate) {
            (this.props as CommonExperienceProps).onExperienceMilestoneTimingUpdate(ExperienceMilestoneEventTypes.initialized,
                this.initializedTracker.getStart());
        }
    }

    /** End the initialized perf timer, and mark the Initialized milestone end time */
    protected endInitializeMilestone(): void {
        if (this.initializedTracker.getEnd() !== -1) {
            return;
        }

        this.initializedTracker.setEnd();

        if ((this.props as CommonExperienceProps).onExperienceMilestoneTimingUpdate) {
            (this.props as CommonExperienceProps).onExperienceMilestoneTimingUpdate(ExperienceMilestoneEventTypes.initialized,
                undefined,
                this.initializedTracker.getEnd());
        }
    }

    /** Start the Visually Reader perf timer, and mark the VisuallyReady milestone start time */
    protected startVisuallyReadyMilestone(): void {
        this.visuallyReadyTracker.setStart();

        if ((this.props as CommonExperienceProps).onExperienceMilestoneTimingUpdate) {
            (this.props as CommonExperienceProps).onExperienceMilestoneTimingUpdate(ExperienceMilestoneEventTypes.visuallyReady,
                this.visuallyReadyTracker.getStart());
        }
    }

    /** End the Visually Reader perf timer, and mark the VisuallyReady milestone end time */
    protected endVisuallyReadyMilestone(): void {
        if (this.visuallyReadyTracker.getEnd() !== -1) {
            return;
        }

        this.visuallyReadyTracker.setEnd();

        if ((this.props as CommonExperienceProps).onExperienceMilestoneTimingUpdate) {
            (this.props as CommonExperienceProps).onExperienceMilestoneTimingUpdate(ExperienceMilestoneEventTypes.visuallyReady,
                undefined,
                this.visuallyReadyTracker.getEnd());
        }
    }

    /** Start the DomComplete perf timer, and mark the DomComplete milestone start time */
    protected startDomCompleteMilestone(): void {
        this.domCompleteTracker.setStart();

        if ((this.props as CommonExperienceProps).onExperienceMilestoneTimingUpdate) {
            (this.props as CommonExperienceProps).onExperienceMilestoneTimingUpdate(ExperienceMilestoneEventTypes.domComplete,
                this.domCompleteTracker.getStart());
        }
    }

    /** End the DomComplete perf timer, and mark the DomComplete milestone end time */
    protected endDomCompleteMilestone(): void {
        if (this.domCompleteTracker.getEnd() !== -1) {
            return;
        }

        this.domCompleteTracker.setEnd();

        if ((this.props as CommonExperienceProps).onExperienceMilestoneTimingUpdate) {
            (this.props as CommonExperienceProps).onExperienceMilestoneTimingUpdate(ExperienceMilestoneEventTypes.domComplete,
                undefined,
                this.domCompleteTracker.getEnd());
        }
    }
}